import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import TenantSidebar from '../../components/tenant/TenantSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toFixed(2);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function TenantBilling() {
  const stripe = useStripe();
  const elements = useElements();
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingPayment, setAddingPayment] = useState(false);
  const [paymentType, setPaymentType] = useState('card');
  const [savingPayment, setSavingPayment] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'checking',
  });

  useEffect(() => {
    Promise.all([
      api.get('/billing/tenant/invoices'),
      api.get('/billing/tenant/subscription'),
      api.get('/billing/tenant/payment-methods'),
    ]).then(([invRes, subRes, pmRes]) => {
      setInvoices(invRes.data.invoices);
      setSubscription(subRes.data.subscription);
      setPaymentMethods(pmRes.data.payment_methods);
    }).catch(() => toast.error('Could not load billing information'))
      .finally(() => setLoading(false));
  }, []);

  const handleAddCard = async () => {
    if (!stripe || !elements) return;
    setSavingPayment(true);
    try {
      const intentRes = await api.post('/billing/tenant/setup-intent');
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error } = await stripe.confirmCardSetup(intentRes.data.client_secret, {
        payment_method: { card: cardElement },
      });
      if (error) throw new Error(error.message);
      toast.success('Card saved successfully!');
      setAddingPayment(false);
      const pmRes = await api.get('/billing/tenant/payment-methods');
      setPaymentMethods(pmRes.data.payment_methods);
    } catch (err) {
      toast.error(err.message || 'Could not save card');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddBankAccount = async () => {
    if (!stripe) return;
    const { account_holder_name, routing_number, account_number, account_type } = bankDetails;
    if (!account_holder_name || !routing_number || !account_number) {
      toast.error('Please fill in all bank account fields');
      return;
    }
    if (routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }
    setSavingPayment(true);
    try {
      const { token, error } = await stripe.createToken('bank_account', {
        country: 'US',
        currency: 'usd',
        routing_number,
        account_number,
        account_holder_name,
        account_holder_type: 'individual',
        account_type,
      });
      if (error) throw new Error(error.message);
      await api.post('/billing/tenant/add-bank-account', { token_id: token.id });
      toast.success('Bank account added! Verification deposits will arrive in 1-2 business days.');
      setAddingPayment(false);
      setBankDetails({ account_holder_name: '', routing_number: '', account_number: '', account_type: 'checking' });
      const pmRes = await api.get('/billing/tenant/payment-methods');
      setPaymentMethods(pmRes.data.payment_methods);
    } catch (err) {
      toast.error(err.message || 'Could not add bank account');
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const overdueInvoice = invoices.find(i => i.status === 'overdue');

  return (
    <div className="app-layout">
      <Topbar role="tenant" />
      <div className="dashboard-layout">
        <TenantSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Billing &amp; Payments</div>
          <div className="page-sub">Manage your payment method, autopay, and billing history.</div>

          {overdueInvoice && (
            <div style={{ background: '#ffeaea', border: '1px solid #f5a0a0', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <strong style={{ color: 'var(--red)' }}>Payment overdue</strong>
              <p style={{ fontSize: 13, color: 'var(--text-sub)', marginTop: 4 }}>
                A payment of ${fmt(overdueInvoice.amount)} is overdue. Please update your payment method.
              </p>
            </div>
          )}

          <div className="two-col">
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-header"><div className="card-title">Current balance</div></div>
              <div className="card-body" style={{ textAlign: 'center', padding: 28 }}>
                <div style={{ fontSize: 44, fontWeight: 800, color: 'var(--navy)' }}>
                  ${subscription ? fmt(subscription.bulknet_price) : '0.00'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-sub)', margin: '6px 0 20px' }}>
                  Due {fmtDate(subscription?.current_period_end)}
                </div>
                {subscription && (
                  <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, marginBottom: 16 }}>
                    Saving ${fmt(subscription.savings_per_month)}/mo vs. retail ${fmt(subscription.retail_price)}/mo
                  </div>
                )}
                <button className="btn btn-primary" style={{ width: '100%', padding: 12 }}>Pay Now</button>
              </div>
            </div>

            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-header">
                <div className="card-title">Payment method</div>
                <button className="btn btn-outline btn-sm" onClick={() => setAddingPayment(!addingPayment)}>
                  {addingPayment ? 'Cancel' : '+ Add payment method'}
                </button>
              </div>
              <div className="card-body">
                {paymentMethods.length > 0 ? paymentMethods.map(pm => (
                  <div key={pm.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>
                      {pm.type === 'us_bank_account' ? 'BANK' : pm.brand?.toUpperCase()}
                    </div>
                    <div>
                      {pm.type === 'us_bank_account' ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>Bank account ending in {pm.last4}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>ACH Direct Debit</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{pm.brand} ending in {pm.last4}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                            {pm.funding === 'debit' ? 'Debit card' : 'Credit card'} · Expires {pm.exp_month}/{pm.exp_year}
                          </div>
                        </>
                      )}
                    </div>
                    {pm.is_default && <span className="badge badge-green" style={{ marginLeft: 'auto' }}>Default</span>}
                  </div>
                )) : (
                  <p style={{ color: 'var(--text-sub)', fontSize: 13 }}>No payment methods saved.</p>
                )}

                {addingPayment && (
                  <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      <button className={`btn btn-sm ${paymentType === 'card' ? 'btn-primary' : 'btn-outline'}`}
                        style={{ flex: 1 }} onClick={() => setPaymentType('card')}>
                        Card or Debit
                      </button>
                      <button className={`btn btn-sm ${paymentType === 'bank' ? 'btn-primary' : 'btn-outline'}`}
                        style={{ flex: 1 }} onClick={() => setPaymentType('bank')}>
                        Bank Account
                      </button>
                    </div>

                    {paymentType === 'card' && (
                      <>
                        <p style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10 }}>
                          We accept all major credit and debit cards — Visa, Mastercard, Amex, and Discover.
                        </p>
                        <div style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
                          <CardElement options={{ style: { base: { fontSize: '14px', color: 'var(--text)' } } }} />
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleAddCard} disabled={savingPayment}>
                          {savingPayment ? 'Saving...' : 'Save Card'}
                        </button>
                      </>
                    )}

                    {paymentType === 'bank' && (
                      <>
                        <div style={{ background: '#f0f9ff', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                          <p style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600, marginBottom: 2 }}>
                            Pay directly from your checking or savings account
                          </p>
                          <p style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                            After adding your account, we will send 2 small verification deposits within 1-2 business days. Return here to confirm the amounts and activate your account.
                          </p>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Account holder name</label>
                          <input className="form-input" placeholder="Full name as it appears on your account"
                            value={bankDetails.account_holder_name}
                            onChange={e => setBankDetails({ ...bankDetails, account_holder_name: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Account type</label>
                          <select className="form-input" value={bankDetails.account_type}
                            onChange={e => setBankDetails({ ...bankDetails, account_type: e.target.value })}>
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Routing number (9 digits)</label>
                          <input className="form-input" placeholder="e.g. 021000021" maxLength={9}
                            value={bankDetails.routing_number}
                            onChange={e => setBankDetails({ ...bankDetails, routing_number: e.target.value.replace(/\D/g, '') })} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Account number</label>
                          <input className="form-input" placeholder="Your account number"
                            value={bankDetails.account_number}
                            onChange={e => setBankDetails({ ...bankDetails, account_number: e.target.value.replace(/\D/g, '') })} />
                        </div>
                        <p style={{ fontSize: 11, color: 'var(--text-sub)', marginBottom: 12 }}>
                          Your banking information is encrypted and secured by Stripe. Affordable Telecom never stores your account details.
                        </p>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleAddBankAccount} disabled={savingPayment}>
                          {savingPayment ? 'Adding account...' : 'Add Bank Account'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <div className="card-title">Billing history</div>
              <button className="btn btn-outline btn-sm">Export CSV</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th><th>Receipt</th></tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No invoices yet</td></tr>
                  )}
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{fmtDate(inv.created_at)}</td>
                      <td>{subscription?.plan_name || 'Service'}</td>
                      <td style={{ fontWeight: 700 }}>${fmt(inv.amount)}</td>
                      <td>
                        <span className={`badge badge-${inv.status === 'paid' ? 'green' : inv.status === 'overdue' ? 'red' : 'orange'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td>
                        {inv.invoice_pdf_url
                          ? <a href={inv.invoice_pdf_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 12 }}>Download</a>
                          : <span style={{ color: 'var(--text-sub)', fontSize: 12 }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
